import firebase from "firebase/app";

export const serviceDomainsCollectionKey = "serviceDomains";
export const analysisCollectionKey = "analysis";
export const systemSettingsCollectionKey = "systemSettings";
export const modelsCollectionKey = "models";

export const DEFAULT_SERVICE_DOMAIN = "school-ai";

export type ContextCompressionConfig = {
  applyToAllSchools: boolean;
  tokenThreshold: number;
  summaryPrefix?: string;
};

export type ServiceDomain = {
  ref: firebase.firestore.DocumentReference;
  data: ServiceDomainData;
};

export type ServiceDomainData = {
  contextCompressionConfig?: ContextCompressionConfig | null;
};

export function convertToServiceDomain(
  data: firebase.firestore.DocumentData,
  ref: firebase.firestore.DocumentReference
): ServiceDomain {
  const _data: ServiceDomainData = {};
  if (data.contextCompressionConfig) {
    _data.contextCompressionConfig = data.contextCompressionConfig;
  }
  return { ref, data: _data };
}

export type AIModelTag =
  | "experimental"
  | "recommended"
  | "general"
  | "emergency";

export const AIModelTagMap: Record<AIModelTag, string> = {
  experimental: "試験運用中",
  recommended: "推奨",
  general: "一般提供版",
  emergency: "緊急用"
};

export type Provider = "openai" | "microsoft" | "amazon" | "google";

export type ModelData = {
  active: boolean;
  value: string;
  displayName: string;
  provider: Provider;
  availableAssistApi?: boolean;
  availableVision?: boolean;
  streaming?: boolean;
  inputCost?: number;
  outputCost?: number;
  anthropic_version?: string;
  customDisplayName?: string;
  tags?: AIModelTag[];
  availableSchoolIds?: string[];
  unavailableSchoolIds?: string[];
};

export type Model = {
  ref: firebase.firestore.DocumentReference;
  data: ModelData;
};

export function convertToModel(
  data: firebase.firestore.DocumentData,
  ref: firebase.firestore.DocumentReference
): Model {
  const _data: ModelData = {
    active: data.active,
    value: data.value,
    displayName: data.displayName,
    provider: data.provider,
    availableAssistApi: data.availableAssistApi ?? false,
    availableVision: data.availableVision ?? false,
    streaming: data.streaming ?? false,
    customDisplayName: data.customDisplayName ?? "",
    availableSchoolIds: Array.isArray(data.availableSchoolIds)
      ? data.availableSchoolIds.filter(
          (schoolId: unknown): schoolId is string =>
            typeof schoolId === "string"
        )
      : [],
    unavailableSchoolIds: Array.isArray(data.unavailableSchoolIds)
      ? data.unavailableSchoolIds.filter(
          (schoolId: unknown): schoolId is string =>
            typeof schoolId === "string"
        )
      : []
  };

  if (data.inputCost) {
    _data.inputCost = data.inputCost;
  }

  if (data.outputCost) {
    _data.outputCost = data.outputCost;
  }

  if (data["anthropic_version"]) {
    _data["anthropic_version"] = data["anthropic_version"];
  }

  if (data.tags) {
    _data.tags = data.tags;
  }

  return { data: _data, ref };
}

export function isModelAvailableToSchool(
  availableSchoolIds?: string[] | null,
  unavailableSchoolIds?: string[] | null,
  schoolId?: string | null
): boolean {
  if (!schoolId) {
    return true;
  }

  if (unavailableSchoolIds?.includes(schoolId)) {
    return false;
  }

  if (!availableSchoolIds || availableSchoolIds.length === 0) {
    return true;
  }

  return availableSchoolIds.includes(schoolId);
}
